import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeOnboardSellLook = 'prep-sell-pill' | 'prep-sell-pill-on';
export type WedgeOnboardSellFieldVariant = 'wedge-sell-area' | 'wedge-sell-field';

const SELL_LOOKS = new Set<string>(['prep-sell-pill', 'prep-sell-pill-on']);
export const isWedgeOnboardSellLook = (look: string): look is WedgeOnboardSellLook => SELL_LOOKS.has(look);
export const isWedgeOnboardSellFieldVariant = (variant: unknown): variant is WedgeOnboardSellFieldVariant => variant === 'wedge-sell-area' || variant === 'wedge-sell-field';

/* Released onboarding sell step (prep-screens-1 OnboardSell): the seven chip groups and the two text fields. The chip is the subcategory pill
   with a 9px vertical padding, the border, ring and fill transition and no touch action; the chosen chip fills with its own hue (the caller sets
   --pill-accent) and wears a two-layer glow of that hue. The pitch field is a 14px padded textarea on the surface fill; the company field is a
   one-line input with 13px by 14px of padding and a 13px radius. Each reads the exact legacy roles, in the order of the source. The caller owns
   the labels, the handlers, the shipped wedge-pill and wedge-onb-field classes (hover and focus paint from them), the --pill-accent value and
   the pop animation. A chip draws no target, anchor or stacking context: it is 44px tall already, so its target would equal its box, and the
   look name starts with prep-, which the shipped no-target rule names. */

const PILL_PADDING = '9px 14px', PILL_MIN = 44, PILL_GAP = 5, PILL_RING = 22, PILL_GLOW = 24, PILL_FONT = 12.5, ROUND = 999;
const AREA_RADIUS = 14, AREA_FONT = 16, AREA_LINE = 1.45, FIELD_PADDING = '13px 14px', FIELD_RADIUS = 13, FIELD_FONT = 15.5;

export function wedgeOnboardSellStyle(look: WedgeOnboardSellLook): CSSProperties {
  const r = wedgeExactRole;
  const on = look === 'prep-sell-pill-on';
  const hue = 'var(--pill-accent)';
  return {
    padding: PILL_PADDING, borderRadius: ROUND, minHeight: PILL_MIN, boxSizing: 'border-box',
    background: on ? hue : r('bgElev'),
    color: on ? r('onAccent') : r('labelSec'),
    border: on ? `1px solid ${hue}` : `1px solid ${r('cardBorder')}`,
    boxShadow: on ? `0 0 0 3px color-mix(in srgb, ${hue} ${PILL_RING}%, transparent), 0 2px 12px color-mix(in srgb, ${hue} ${PILL_GLOW}%, transparent)` : 'none',
    transition: `background 160ms ${r('ease')}, border-color 160ms ${r('ease')}, box-shadow 160ms ${r('ease')}, transform 140ms ${r('ease')}`,
    cursor: 'pointer', fontSize: PILL_FONT, fontWeight: 600, fontFamily: r('font'),
    display: 'inline-flex', alignItems: 'center', gap: PILL_GAP,
  };
}

export function wedgeOnboardSellFieldStyle(variant: WedgeOnboardSellFieldVariant): CSSProperties {
  const r = wedgeExactRole;
  if (variant === 'wedge-sell-area') {
    return {
      width: '100%', padding: 14, background: r('surface'), border: `1px solid ${r('cardBorder')}`, borderRadius: AREA_RADIUS,
      fontSize: AREA_FONT, color: r('label'), fontFamily: r('font'), lineHeight: AREA_LINE, resize: 'none', outline: 0, boxSizing: 'border-box',
    };
  }
  return {
    width: '100%', padding: FIELD_PADDING, background: r('surface'), border: `1px solid ${r('cardBorder')}`, borderRadius: FIELD_RADIUS,
    fontSize: FIELD_FONT, color: r('label'), fontFamily: r('font'), outline: 0, boxSizing: 'border-box',
  };
}

export const wedgeOnboardSellDrawsHit = (look: string): boolean => !isWedgeOnboardSellLook(look);
